import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { LIST_POLL_MS, useLiveList, useLiveSessionState, useSocket } from '../../SocketContext';
import { useLinkedPlayers } from '../../api/useLinkedPlayers';
import { ErrorState, ListeningState, Marking, PlayerChip, SearchField, Skel } from '../ui';
import { clockOf, gameLabel, sessionStateOf } from './liveFormat';
import './live.css';

/**
 * LiveSessionTile — one live session on the Live landing grid: state, game, who, slot tags.
 * Status is only known for sessions this browser has joined; otherwise the tile says "Live".
 * (Tracker summary and multi-slot rosters arrive with the multiplayer relay events.)
 */
export function LiveSessionTile({ session, playerName }) {
    const state = useLiveSessionState(session.sessionId, { watch: false });
    const st = sessionStateOf(state?.status);
    const name = playerName || 'Unknown player';
    return (
        <Link to={`/live/${session.sessionId}`} className="lv-tile" aria-label={`${gameLabel(session.game) || 'Game'}, ${st.label}, ${name}`}>
            <span className="lv-tile__top">
                <span className={'eg-dot eg-dot--lg' + (st.pulse ? ' eg-dot--pulse' : '')} style={{ background: st.dot }} />
                <span className="lv-tile__state" style={{ color: st.ink }}>{st.label}</span>
                <span className="eg-spacer" />
                {session.startTime ? <span className="lv-tile__start">Started {clockOf(session.startTime)}</span> : null}
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="lv-tile__game">{gameLabel(session.game) || 'Unknown game'}</span>
                <span className="lv-tile__people">{name}</span>
            </span>
            <span className="lv-tile__slots">
                <PlayerChip variant="tag" slot={1} name={name} />
                <span className="eg-small eg-secondary" style={{ marginLeft: 4 }}>Solo</span>
            </span>
        </Link>
    );
}

export default function LiveOverview() {
    const { user } = useAuth();
    const isPlayer = user?.role === 'player';
    const list = useLiveList();
    const { connected, connectError, reconnect } = useSocket();
    const linked = useLinkedPlayers();
    const [q, setQ] = useState('');

    const mine = useMemo(() => (isPlayer ? list.sessions.filter((s) => s.userId === user?.user_id) : list.sessions), [isPlayer, list.sessions, user]);
    const nameFor = (s) => (s.userId === user?.user_id ? user?.name : linked.nameOf(s.userId));
    const query = q.trim().toLowerCase();
    const shown = mine.filter((s) => !query || `${s.game || ''} ${gameLabel(s.game) || ''} ${nameFor(s) || ''}`.toLowerCase().includes(query));

    // Waiting facts: who can appear here (a verified link), the links still pending, when the relay last answered.
    const linkFacts = useMemo(() => {
        if (isPlayer || linked.loading || linked.error) return null;
        const players = new Set(linked.players.filter((p) => p.verified).map((p) => p.user_id)).size;
        const pending = linked.players.filter((p) => !p.verified).length;
        return [`${players} ${players === 1 ? 'player' : 'players'}`, `${pending} ${pending === 1 ? 'link' : 'links'} pending`];
    }, [isPlayer, linked.loading, linked.error, linked.players]);
    const checked = list.at ? `Checked ${new Date(list.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : null;

    const failed = !connected && (!!connectError || list.error) && !list.sessions.length;
    const loading = !failed && !list.loaded;
    const ready = !failed && !loading;

    return (
        <div className="eg-page eg-page--tight" data-screen-label="Live overview">
            <div className="lv-head">
                <h1 className="eg-h1">Live</h1>
                {ready ? <span className="lv-count">{mine.length} in progress</span> : null}
                <span className="eg-spacer" />
                {ready && !isPlayer && mine.length > 0 ? (
                    <div className="lv-search"><SearchField value={q} onChange={setQ} placeholder="Search patient or game" label="Search live sessions" /></div>
                ) : null}
            </div>

            {loading ? (
                <div className="lv-tiles" aria-busy="true" aria-label="Loading live sessions">
                    <Skel h={236} raised />
                    <Skel h={236} raised />
                    <Skel h={236} raised />
                </div>
            ) : null}

            {failed ? (
                <ErrorState title="Live sessions didn’t load" kicker={null} onRetry={reconnect}>
                    The portal relay isn’t responding{connectError ? ` (${connectError})` : ''}.
                </ErrorState>
            ) : null}

            {ready && !mine.length ? (
                <ListeningState
                    // aria-live off: the check time changes every poll, the status region must not read it out
                    details={<Marking aria-live="off" name="Live sessions" lines={[linkFacts, [checked, `every ${LIST_POLL_MS / 1000} s`]]} />}
                />
            ) : null}

            {ready && mine.length ? (
                <>
                    <div className="lv-tiles">
                        {shown.map((s) => <LiveSessionTile key={s.sessionId} session={s} playerName={nameFor(s)} />)}
                    </div>
                    {!shown.length ? <div className="eg-body eg-secondary" style={{ padding: '32px 0' }}>No live sessions match “{q}”.</div> : null}
                </>
            ) : null}
        </div>
    );
}
